import { authService } from "@shared/services/authService";

/**
 * Utilidades para la gestión de tokens de autenticación
 */

// Tipos para información del token
export interface TokenInfo {
  hasToken: boolean;
  isExpired: boolean;
  expiration?: Date;
  payload?: unknown;
  timeUntilExpiration?: number; // en milisegundos
}

export interface TokenValidation {
  isValid: boolean;
  payload?: unknown;
  error?: string;
}

/**
 * Clase para gestión avanzada de tokens
 */
export class TokenManager {
  private static instance: TokenManager;
  private refreshInterval: number | null = null;

  private constructor() {}

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  /**
   * Obtener información completa del token
   */
  getTokenInfo(): TokenInfo {
    const info = authService.getTokenInfo();
    const timeUntilExpiration = info.expiration
      ? info.expiration.getTime() - Date.now()
      : undefined;

    return {
      ...info,
      timeUntilExpiration,
    };
  }

  /**
   * Validar token con información detallada
   */
  validateToken(): TokenValidation {
    return authService.validateToken();
  }

  /**
   * Verificar si el token está próximo a expirar
   */
  isTokenExpiringSoon(minutesThreshold: number = 5): boolean {
    const info = this.getTokenInfo();

    if (!info.expiration || info.isExpired) {
      return true;
    }

    const thresholdMs = minutesThreshold * 60 * 1000;
    return info.timeUntilExpiration
      ? info.timeUntilExpiration <= thresholdMs
      : true;
  }

  /**
   * Obtener tiempo restante hasta la expiración
   */
  getTimeUntilExpiration(): number | null {
    const info = this.getTokenInfo();
    return info.timeUntilExpiration || null;
  }

  /**
   * Formatear tiempo restante en formato legible
   */
  getFormattedTimeUntilExpiration(): string | null {
    const timeMs = this.getTimeUntilExpiration();

    if (!timeMs || timeMs <= 0) {
      return "Expirado";
    }

    const minutes = Math.floor(timeMs / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}d ${hours % 24}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else {
      return `${minutes}m`;
    }
  }

  /**
   * Iniciar monitoreo automático del token
   */
  startTokenMonitoring(intervalMs: number = 60000): void {
    this.stopTokenMonitoring(); // Detener monitoreo anterior si existe

    this.refreshInterval = setInterval(() => {
      const info = this.getTokenInfo();

      if (!info.hasToken || info.isExpired) {
        authService.logout();
        this.stopTokenMonitoring();
        return;
      }

      if (this.isTokenExpiringSoon(10)) {
        // 10 minutos antes
        authService.refreshToken().catch(() => {
          this.stopTokenMonitoring();
        });
      }
    }, intervalMs);
  }

  /**
   * Detener monitoreo automático del token
   */
  stopTokenMonitoring(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  /**
   * Verificar si el usuario tiene un rol específico
   */
  hasRole(role: string): boolean {
    const validation = this.validateToken();
    if (!validation.isValid || !validation.payload) return false;

    const payload = validation.payload as { role?: string };
    return payload.role === role;
  }

  /**
   * Obtener información del usuario desde el token
   */
  getUserFromToken(): {
    id?: string;
    email?: string;
    role?: string;
    name?: string;
  } | null {
    const validation = this.validateToken();
    if (!validation.isValid || !validation.payload) return null;

    const payload = validation.payload as {
      id?: string;
      email?: string;
      role?: string;
      name?: string;
    };
    return payload;
  }

  /**
   * Limpiar todos los datos de autenticación
   */
  clearAuth(): void {
    this.stopTokenMonitoring();
    authService.logout();
  }
}

// Instancia singleton
export const tokenManager = TokenManager.getInstance();

/**
 * Funciones de utilidad independientes
 */

/**
 * Verificar si hay un token válido en localStorage
 */
export function hasValidToken(): boolean {
  return authService.isAuthenticated();
}

/**
 * Obtener token del localStorage
 */
export function getStoredToken(): string | null {
  return authService.getToken();
}

/**
 * Guardar token en localStorage
 */
export function storeToken(token: string): void {
  authService.setToken(token);
}

/**
 * Verificar si el token expira en los próximos X minutos
 */
export function isTokenExpiringIn(minutes: number): boolean {
  const info = tokenManager.getTokenInfo();

  if (!info.expiration || info.isExpired) {
    return true;
  }

  const thresholdMs = minutes * 60 * 1000;
  return info.timeUntilExpiration
    ? info.timeUntilExpiration <= thresholdMs
    : true;
}

/**
 * Decodificar token JWT manualmente
 */
export function decodeJWT(token: string): unknown {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    throw new Error("Token JWT inválido");
  }
}

/**
 * Verificar si el token es válido sin hacer llamadas al servidor
 */
export function isTokenValid(token?: string): boolean {
  try {
    const tokenToCheck = token || getStoredToken();

    if (!tokenToCheck) {
      return false;
    }

    const payload = decodeJWT(tokenToCheck) as { exp: number };
    const expirationTime = payload.exp * 1000;

    return Date.now() < expirationTime;
  } catch {
    return false;
  }
}
